/**
 * WHAT A 401 MEANS DEPENDS ON WHICH TOKEN THE REQUEST CARRIED.
 *
 * The interceptor used to log out on every 401. `logout()` clears the `token`
 * cookie and the persisted store, and both are shared by every tab on the
 * origin — but each tab keeps its own in-memory copy of the store, which does
 * not follow the others. So one stale tab (opened while logged out, or still
 * holding the token from before a re-login elsewhere) polling an authed
 * endpoint would get a 401 and wipe the session the user had just created in
 * another tab. Measured on /message, whose thread polls every five seconds:
 * the user was logged out about a minute after every login.
 *
 * The cookie is the cross-tab truth, so the verdict compares the token the
 * request was SENT with against the token the cookie holds NOW.
 */
export type UnauthorizedVerdict =
  /** The session this request used is the live one, and the server rejected it. */
  | "end-session"
  /** Another tab holds a newer session; adopt it instead of destroying it. */
  | "adopt-newer"
  /** Nobody is signed in: ask for a login, but there is nothing to clear. */
  | "prompt-login";

export function resolveUnauthorized(sentToken: string | null, sharedToken: string | null): UnauthorizedVerdict {
  if (sharedToken && sharedToken !== sentToken) return "adopt-newer";
  if (sentToken) return "end-session";
  return "prompt-login";
}

/** The `token` cookie `useAuthStore.setToken` writes, or null. */
export function readTokenCookie(cookie: string): string | null {
  for (const part of cookie.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === "token") return rest.join("=") || null;
  }
  return null;
}
